import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Mention, MentionsInput, OnChangeHandlerFunc, SuggestionDataItem } from 'react-mentions';
import { useQuery } from '@tanstack/react-query';
import { commentClient, MentionableUserResponse } from 'src/lib/services/api/comment-api';
import { cn } from 'src/lib/utils';
import { mapPlainCaretToMarkup } from 'src/lib/utils/render-mentions';
import './mention-textarea.css';

/**
 * Imperative API exposed via `ref`. Use this for caret-aware operations on
 * the markup string — directly slicing `value` at `textareaRef.selectionStart`
 * splits `@[Name](uuid)` tokens (caret is plain-text, value is markup).
 */
type MentionTextareaHandle = {
  insertAtCaret: (text: string) => void;
  focus: () => void;
};

type OnKeyDownHandler = (
  event: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>,
) => void;

type Props = {
  requestId: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onKeyDown?: OnKeyDownHandler;
  placeholder?: string;
  rows?: number;
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
};

const MENTIONABLE_USERS_STALE_MS = 60 * 1000;

const renderSuggestion = (
  suggestion: SuggestionDataItem,
  _search: string,
  highlightedDisplay: React.ReactNode,
  _index: number,
  focused: boolean,
) => {
  const data = suggestion as SuggestionDataItem & {
    avatar?: string | null;
    type?: 'editor' | 'customer';
    is_assigned?: boolean;
  };

  return (
    <div
      className={cn(
        'tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-cursor-pointer',
        focused && 'tw-bg-neutral-100',
      )}
    >
      {data.avatar ? (
        <img
          src={data.avatar}
          alt=""
          className="tw-h-6 tw-w-6 tw-rounded-full tw-object-cover"
        />
      ) : (
        <div className="tw-h-6 tw-w-6 tw-rounded-full tw-bg-neutral-200 tw-flex tw-items-center tw-justify-center tw-text-xs tw-text-neutral-600">
          {String(suggestion.display ?? '?').charAt(0).toUpperCase()}
        </div>
      )}
      <span className="tw-flex-1 tw-text-sm">
        {highlightedDisplay}
        {data.is_assigned ? (
          <span className="tw-ml-1 tw-text-xs tw-text-text-subtle">(assigned)</span>
        ) : null}
      </span>
      {data.type ? (
        <span className="tw-text-xs tw-text-text-subtle tw-capitalize">{data.type}</span>
      ) : null}
    </div>
  );
};

/**
 * MentionTextarea
 *
 * Textarea with @mention autocomplete backed by `react-mentions`. Suggestions
 * come from the `/comments/{request}/mentionable-users` endpoint and the
 * serialized output is `@[Display Name](user-uuid)` — identical to the regex
 * the backend parser expects (RequestRevisionComment::parseMentionsFromText).
 *
 * Keyboard handling: when the suggestion list is open, `react-mentions`
 * intercepts Up/Down/Enter/Escape and sets `defaultPrevented=true` on the
 * synthetic event. The parent's `onKeyDown` (typically Enter-to-submit) must
 * inspect `event.defaultPrevented` and bail out, otherwise selecting a
 * suggestion with Enter also submits the form.
 *
 * Caret-aware insertion (emoji picker, help-me-write, etc.) MUST go through
 * the imperative `insertAtCaret` ref method — see MentionTextareaHandle.
 */
const MentionTextarea = forwardRef<MentionTextareaHandle, Props>(function MentionTextarea(
  {
    requestId,
    value,
    onChange,
    onBlur,
    onKeyDown,
    placeholder,
    rows = 6,
    className,
    disabled,
    autoFocus,
  },
  ref,
) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { data: mentionableUsers = [] } = useQuery<MentionableUserResponse[]>({
    queryKey: ['request', requestId, 'mentionable-users'],
    queryFn: () => commentClient.mentionableUsers(requestId),
    staleTime: MENTIONABLE_USERS_STALE_MS,
    refetchOnWindowFocus: false,
    enabled: !!requestId,
  });

  const suggestions: SuggestionDataItem[] = React.useMemo(
    () =>
      mentionableUsers.map((u) => ({
        id: u.id,
        display: u.display,
        // Extra fields forwarded to renderSuggestion via cast.
        avatar: u.avatar,
        type: u.type,
        is_assigned: u.is_assigned,
      })) as (SuggestionDataItem & {
        avatar?: string | null;
        type?: 'editor' | 'customer';
        is_assigned?: boolean;
      })[],
    [mentionableUsers],
  );

  useImperativeHandle(
    ref,
    () => ({
      insertAtCaret(text: string) {
        const el = textareaRef.current;
        const currentValue = value ?? '';
        // Without a textarea reference we can't read the caret — fall back
        // to appending at the end, which keeps the mention markup intact.
        const plainCaret = el ? el.selectionStart ?? currentValue.length : currentValue.length;
        const markupCaret = mapPlainCaretToMarkup(currentValue, plainCaret);

        const next = currentValue.slice(0, markupCaret) + text + currentValue.slice(markupCaret);
        onChange(next);

        // Restore caret after React renders the new controlled value. The
        // new plain-text caret is the old one plus the inserted text length
        // (the inserted text never contains mention markup).
        if (el) {
          const newPlainCaret = plainCaret + text.length;
          setTimeout(() => {
            el.focus();
            el.selectionStart = newPlainCaret;
            el.selectionEnd = newPlainCaret;
          }, 0);
        }
      },
      focus() {
        textareaRef.current?.focus();
      },
    }),
    [value, onChange],
  );

  // react-mentions ships its own OnChangeHandlerFunc signature
  // (event, newValue, newPlainTextValue, mentions). We only need newValue
  // for the form, but the typed signature lets us drop the `as any` cast on
  // onChange and keep TS honest at the boundary.
  const handleChange: OnChangeHandlerFunc = (_event, newValue) => onChange(newValue);

  return (
    <MentionsInput
      value={value ?? ''}
      onChange={handleChange}
      onBlur={onBlur ? () => onBlur() : undefined}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      rows={rows}
      className={cn('mention-textarea', className)}
      inputRef={(el: HTMLTextAreaElement | HTMLInputElement | null) => {
        textareaRef.current = el as HTMLTextAreaElement | null;
      }}
      disabled={disabled}
      autoFocus={autoFocus}
      a11ySuggestionsListLabel="Mentionable users"
      allowSuggestionsAboveCursor
      // The chat input is anchored to the bottom of the panel, so there's no
      // room below it. react-mentions' auto-placement opened the list
      // downward anyway and it got clipped by the viewport edge — only the
      // first couple of suggestions stayed visible (#2125). Force it above
      // so the whole list (which scrolls past its max-height) is reachable.
      forceSuggestionsAboveCursor
    >
      <Mention
        trigger="@"
        markup="@[__display__](__id__)"
        data={suggestions}
        displayTransform={(_id, display) => `@${display}`}
        renderSuggestion={renderSuggestion}
        appendSpaceOnAdd
        className="mention-textarea__mention"
      />
    </MentionsInput>
  );
});

export { MentionTextarea };
export type { MentionTextareaHandle };
